/**
 * Compact AI capacity gauges for the Cockpit.
 *
 * Presentation only. Figures come from lib/ai-usage-view; absent percentages stay
 * unknown and never receive a fake arc.
 */
import type { ReactElement } from 'react';
import { AI_USAGE_GUIDANCE, isExternalAiStatusMeter, isLocalRuntimeMeter, type AiCapacityMeter, type UsageAdvisory } from '../lib/ai-usage-view';
import { AiUsageGauge } from './AiUsageGauge';

export interface AiCapacityStripProps {
  readonly meters: readonly AiCapacityMeter[];
  readonly advisories?: readonly UsageAdvisory[];
  readonly empty?: string;
  readonly selectedKey?: string | null;
  readonly onSelect?: (key: string) => void;
  /** Visual-only mode for wide glance surfaces such as the bottom Live Dock. */
  readonly compact?: boolean;
}

export function AiCapacityStrip({
  meters,
  advisories = [],
  selectedKey = null,
  onSelect,
  compact = false,
  empty = 'No AI usage readings yet. Gauges appear when a provider states an allowance, or when a tool reports it is out of usage.',
}: AiCapacityStripProps): ReactElement {
  return (
    <section className="ai-capacity-strip stack" aria-label="AI usage and capacity">
      <header className="row spread">
        <h2>AI usage &amp; capacity</h2>
        <small className="muted">{meters.length === 0 ? 'none yet' : `${meters.length} gauge${meters.length === 1 ? '' : 's'}`}</small>
      </header>

      {meters.length === 0 ? (
        <p className="muted ai-capacity-empty">{empty}</p>
      ) : (
        <ul className="ai-capacity-meters" aria-label="Provider capacity gauges">
          {meters.map((meter) => (
            <li key={meter.key} className="ai-capacity-tile">
              {onSelect && <button type="button" className="ai-capacity-select"
                aria-label={isLocalRuntimeMeter(meter)
                  ? `Inspect ${meter.title} on ${meter.localRuntime.nodeName}`
                  : isExternalAiStatusMeter(meter)
                    ? `Inspect ${meter.title} status and capacity`
                    : `Inspect ${meter.model ?? meter.title} usage`}
                title={isLocalRuntimeMeter(meter)
                  ? `${meter.title} · ${meter.localRuntime.nodeName} · Local / Unlimited`
                  : isExternalAiStatusMeter(meter)
                    ? `${meter.title} · ${meter.detailLine}`
                    : `${meter.model ?? meter.title} · ${meter.provider} · ${meter.runtime ?? 'Unknown runtime'} · ${meter.measurementPhrase}`}
                aria-pressed={selectedKey === meter.key}
                onClick={() => onSelect(meter.key)} />}
              <AiUsageGauge meter={meter} size="compact" />
            </li>
          ))}
        </ul>
      )}

      {!compact && advisories.length > 0 && (
        <ul className="ai-capacity-advisories" aria-label="Overseer usage advice">
          {advisories.slice(0, 4).map((item, index) => (
            <li key={`${item.meterKey ?? 'general'}:${index}`} className={`ai-advisory severity-${item.severity}`}>
              {item.text}
            </li>
          ))}
        </ul>
      )}

      {!compact && <p className="muted ai-capacity-guidance">{AI_USAGE_GUIDANCE}</p>}
    </section>
  );
}
